import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { HomeownerPoolDetails } from '@/components/admin/homeowner-pool-details';
import { PageHeader } from '@/components/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  findHomeownerPoolUserBySub,
  listHomeownerPoolUsers,
  selectExactHomeownerPoolUser,
  selectHomeownerPoolError,
  selectHomeownerPoolErrorKind,
  selectHomeownerPoolHasFetched,
  selectHomeownerPoolHasMore,
  selectHomeownerPoolLoading,
  selectHomeownerPoolNextToken,
  selectHomeownerPoolUsers,
  type HomeownerPoolQueryField,
  type HomeownerPoolUser,
} from '@/features/homeowner-users-pool';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

const PAGE_LIMIT = 25;

function getUserKey(user: HomeownerPoolUser): string {
  return user.sub?.trim() || user.username?.trim() || '';
}

export default function HomeownerUsersPoolPage() {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const users = useAppSelector(selectHomeownerPoolUsers);
  const exactUser = useAppSelector(selectExactHomeownerPoolUser);
  const nextToken = useAppSelector(selectHomeownerPoolNextToken);
  const hasMore = useAppSelector(selectHomeownerPoolHasMore);
  const isLoading = useAppSelector(selectHomeownerPoolLoading);
  const hasFetched = useAppSelector(selectHomeownerPoolHasFetched);
  const error = useAppSelector(selectHomeownerPoolError);
  const errorKind = useAppSelector(selectHomeownerPoolErrorKind);

  const subValues = searchParams.getAll('sub');
  const hasSubParameter = searchParams.has('sub');
  const hasUnsupportedRouteParameters = [...searchParams.keys()].some((key) => key !== 'sub');
  const invalidRouteSearch =
    hasUnsupportedRouteParameters || subValues.length > 1 || (hasSubParameter && !subValues[0]?.trim());
  const exactSub = !invalidRouteSearch ? subValues[0]?.trim() : undefined;
  const isExactLookup = Boolean(exactSub);

  const [queryField, setQueryField] = useState<HomeownerPoolQueryField>('phoneNumber');
  const [queryValue, setQueryValue] = useState('');
  const [activeQuery, setActiveQuery] = useState<`${HomeownerPoolQueryField}:${string}` | undefined>();
  const [selectedKey, setSelectedKey] = useState('');

  useEffect(() => {
    if (invalidRouteSearch) return;
    if (exactSub) void dispatch(findHomeownerPoolUserBySub(exactSub));
    else void dispatch(listHomeownerPoolUsers({ limit: PAGE_LIMIT }));
  }, [dispatch, exactSub, invalidRouteSearch]);

  useEffect(() => {
    if (users.length === 0) {
      setSelectedKey('');
      return;
    }
    if (users.some((user) => getUserKey(user) === selectedKey)) return;
    setSelectedKey(getUserKey(users[0]));
  }, [selectedKey, users]);

  const selectedUser = useMemo(
    () => users.find((user) => getUserKey(user) === selectedKey) ?? null,
    [selectedKey, users],
  );

  const runList = async (query?: `${HomeownerPoolQueryField}:${string}`) => {
    await dispatch(listHomeownerPoolUsers({ limit: PAGE_LIMIT, query })).unwrap();
  };

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = queryValue.trim();
    if (!value || isLoading) return;
    const query: `${HomeownerPoolQueryField}:${string}` = `${queryField}:${value}`;
    setActiveQuery(query);
    void runList(query).catch(() => undefined);
  };

  const clearSearch = () => {
    if (isLoading) return;
    setQueryValue('');
    setActiveQuery(undefined);
    void runList().catch(() => undefined);
  };

  const refresh = () => {
    if (isLoading || invalidRouteSearch) return;
    if (exactSub) void dispatch(findHomeownerPoolUserBySub(exactSub));
    else void runList(activeQuery).catch(() => undefined);
  };

  const loadMore = () => {
    if (isLoading || !hasMore || !nextToken) return;
    void dispatch(listHomeownerPoolUsers({ limit: PAGE_LIMIT, nextToken, query: activeQuery }))
      .unwrap()
      .catch(() => undefined);
  };

  const effectiveError = invalidRouteSearch ? 'That homeowner identity search is not valid.' : error;
  const effectiveErrorKind = invalidRouteSearch ? 'invalid-search' : errorKind;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Homeowner Users Pool"
        description="Inspect homeowner authentication identities through the guarded Auth service. This is read-only."
        right={
          <Button
            type="button"
            variant="outline"
            onClick={refresh}
            disabled={isLoading || invalidRouteSearch || effectiveErrorKind === 'access-denied'}
          >
            {isLoading ? 'Loading…' : 'Refresh'}
          </Button>
        }
      />

      {effectiveError ? (
        <Card className={effectiveErrorKind === 'access-denied' ? 'border-destructive/50' : undefined}>
          <CardContent className="pt-6">
            <p role="alert" className={effectiveErrorKind === 'access-denied' ? 'text-destructive' : ''}>
              {effectiveError}
            </p>
            {(isExactLookup || invalidRouteSearch) && effectiveErrorKind !== 'access-denied' ? (
              <Button type="button" variant="link" className="mt-2 px-0" asChild>
                <Link to="/admin/homeowner-users-pool">Open homeowner pool list</Link>
              </Button>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {isExactLookup ? (
        exactUser ? (
          <HomeownerPoolDetails user={exactUser} />
        ) : !effectiveError ? (
          <Card>
            <CardContent className="flex items-center gap-3 pt-6">
              {isLoading ? <LoadingSpinner size={24} /> : null}
              <p className="text-muted-foreground text-sm">
                {isLoading ? 'Loading homeowner authentication identity…' : 'No homeowner identity loaded.'}
              </p>
            </CardContent>
          </Card>
        ) : null
      ) : !invalidRouteSearch && effectiveErrorKind !== 'access-denied' ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Exact pool search</CardTitle>
              <CardDescription>Search one exact username, email address, or E.164 phone number.</CardDescription>
            </CardHeader>
            <CardContent>
              <form className="flex flex-col gap-3 md:flex-row" onSubmit={submitSearch}>
                <Select value={queryField} onValueChange={(value) => setQueryField(value as HomeownerPoolQueryField)}>
                  <SelectTrigger className="md:w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="phoneNumber">Phone number</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="username">Username</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  value={queryValue}
                  onChange={(event) => setQueryValue(event.target.value)}
                  placeholder={queryField === 'phoneNumber' ? '+14165550199' : 'Exact value'}
                  autoComplete="off"
                />
                <Button type="submit" disabled={!queryValue.trim() || isLoading}>
                  Search
                </Button>
                {activeQuery ? (
                  <Button type="button" variant="outline" onClick={clearSearch} disabled={isLoading}>
                    Clear
                  </Button>
                ) : null}
              </form>
            </CardContent>
          </Card>

          <div className="grid gap-6 lg:grid-cols-[420px_1fr]">
            <Card>
              <CardHeader>
                <CardTitle>Pool users</CardTitle>
                <CardDescription>
                  {activeQuery ? 'Exact filtered results.' : 'Homeowner authentication identities.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {users.length === 0 ? (
                  <div className="flex items-center gap-3">
                    {isLoading ? <LoadingSpinner size={24} /> : null}
                    <p className="text-muted-foreground text-sm">
                      {isLoading ? 'Loading homeowner users…' : hasFetched ? 'No users found.' : 'No users loaded.'}
                    </p>
                  </div>
                ) : (
                  users.map((user, index) => {
                    const key = getUserKey(user) || `homeowner-${index}`;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setSelectedKey(key)}
                        className={[
                          'w-full rounded-md border p-3 text-left transition',
                          key === selectedKey ? 'border-primary bg-primary/5' : 'hover:bg-muted/40',
                        ].join(' ')}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium">
                              {user.phoneNumber || user.email || user.username || 'Unnamed identity'}
                            </p>
                            <p className="text-muted-foreground truncate text-xs">
                              {user.email || user.username || user.sub || 'No secondary identifier'}
                            </p>
                          </div>
                          <Badge variant={user.enabled === false ? 'destructive' : 'outline'}>
                            {user.enabled === false ? 'Disabled' : user.userStatus || 'Unknown'}
                          </Badge>
                        </div>
                      </button>
                    );
                  })
                )}

                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={loadMore}
                  disabled={isLoading || !hasMore}
                >
                  {isLoading ? 'Loading…' : hasMore ? 'Load more' : 'No more users'}
                </Button>
              </CardContent>
            </Card>

            {selectedUser ? (
              <HomeownerPoolDetails user={selectedUser} />
            ) : (
              <Card>
                <CardContent className="pt-6">
                  <p className="text-muted-foreground text-sm">Select a homeowner identity to inspect its details.</p>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
